import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function PurchasingBehavior() {
  const locationData = {
    labels: [
      'From a big-box retailer (Target, Walmart, Costco)',
      'Online retailers (Amazon, Chewy)',
      'Chain pet stores (Petco&m, Petsm)',
      'Grocery stores',
      'Privately owned/small pet stores',
      'Other(s)'
    ],
    datasets: [{
      data: [37, 28, 26, 13, 9, 5],
      backgroundColor: '#8BC34A',
      barThickness: 30,
    }]
  };

  const frequencyData = {
    labels: [
      'Once a month',
      'Every 2 weeks',
      'Less than once monthly',
      'Once a week',
      'Multiple times per week'
    ],
    datasets: [{
      data: [45, 25, 18, 11, 5],
      backgroundColor: '#8BC34A',
      barThickness: 30,
    }]
  };

  const locationOptions:ChartOptions<"bar"> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function(context:any) {
            return context.parsed.x + '%';
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 40,
        ticks: {
          stepSize: 10,
          callback: function(value:any) {
            return value;
          },
          font: {
            size: 11
          }
        },
        grid: {
          display: true,
          color: '#e0e0e0'
        },
        title: {
          display: true,
          text: '% of respondents',
          font: {
            size: 11,
            weight: 'normal'
          }
        }
      },
      y: {
        ticks: {
          font: {
            size: 11
          },
          crossAlign: 'far',
          padding: 5
        },
        grid: {
          display: false
        }
      }
    },
    layout: {
      padding: {
        right: 10
      }
    }
  };

  const frequencyOptions:ChartOptions<"bar"> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function(context:any) {
            return context.parsed.x + '%';
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 50,
        ticks: {
          stepSize: 10,
          callback: function(value:any) {
            return value;
          },
          font: {
            size: 11
          }
        },
        grid: {
          display: true,
          color: '#e0e0e0'
        },
        title: {
          display: true,
          text: '% of respondents',
          font: {
            size: 11,
            weight: 'normal'
          }
        }
      },
      y: {
        ticks: {
          font: {
            size: 11
          },
          crossAlign: 'far',
          padding: 5
        },
        grid: {
          display: false
        }
      }
    },
    layout: {
      padding: {
        right: 10
      }
    }
  };

  return (
 <div className="flex flex-col h-full items-center w-full">
        <h1 className="relative inline-block text-white text-center font-extrabold 
  max-w-4xl text-2xl lg:text-4xl font-nunito mb-4 
  bg-gradient-to-r from-[#92D050] to-[#8CC54F] 
  px-6 py-2
  before:content-[''] before:absolute before:left-0 before:top-1/2 
  before:-translate-y-1/2 before:-translate-x-full
  before:border-y-[16px] before:border-r-[16px] before:border-y-transparent 
  before:border-r-[#92D050]

  after:content-[''] after:absolute after:right-0 after:top-1/2 
  after:-translate-y-1/2 after:translate-x-full
  after:border-y-[16px] after:border-l-[16px] after:border-y-transparent 
  after:border-l-[#8CC54F]">Purchasing Behavior</h1>
    

      {/* Charts Container */}
      <div className="grid grid-cols-2 gap-8 mb-6 w-full">
        {/* Left Chart */}
        <div>
          <h2 className="text-base font-bold mb-4">Primary Location of Dog Food Purchases</h2>
           <div className="h-[320px]">
            <Bar data={locationData} options={locationOptions} />
          </div>
        </div>

        {/* Right Chart */}
        <div>
          <h2 className="text-base font-bold mb-4">Dog Food Purchase Frequency</h2>
          <div className="h-[320px]">
            <Bar data={frequencyData} options={frequencyOptions} />
          </div>
        </div>
      </div>

      {/* Insights Section */}
      <div className="border-2 border-green-400 bg-[#f3fadc] rounded-xl p-2 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <div className="flex items-start">
              <span className="text-green-600 mr-2 font-bold">•</span>
              <p className="text-sm leading-relaxed">
                <span className="font-bold">Big-box stores dominate dog food purchasing (37%)</span>, especially in rural regions.
              </p>
            </div>
            <div className="flex items-start">
              <span className="text-green-600 mr-2 font-bold">•</span>
              <p className="text-sm leading-relaxed">
                <span className="font-bold">Online retailers account for 22% of purchases</span>, driven by suburban and female owners.
              </p>
            </div>
            <div className="flex items-start">
              <span className="text-green-600 mr-2 font-bold">•</span>
              <p className="text-sm leading-relaxed">
                <span className="font-bold">Nearly half (49%) purchase dog food monthly</span>, showing a stable buying cycle.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-start">
              <span className="text-green-600 mr-2 font-bold">•</span>
              <p className="text-sm leading-relaxed">
                <span className="font-bold">One in four purchase every two weeks (25%)</span>, especially younger owners and multi-dog households.
              </p>
            </div>
            <div className="flex items-start">
              <span className="text-green-600 mr-2 font-bold">•</span>
              <p className="text-sm leading-relaxed">
                <span className="font-bold">Less frequent buyers (less than once monthly, 21%)</span>, pointing to predictable replenishment behavior.
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}